import { DndContext, useDroppable } from "@dnd-kit/core";
import { CanvasMargin } from "@/appComponents/Blocks/Composites/ChartCanvas/CanvasMargin";
import { useEffect, useRef, useState } from "react";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  snapCenterToCursor
} from "@dnd-kit/modifiers";
import { useTopic } from "@/hooks/useTopic";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { GetBGMgr, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { isCanvasRoute } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { isNullish } from "@theorgchartdev/storybook";
import { ChartBoxContextToolbar } from "@/appComponents/Blocks/Menus/ChartBoxContextToolbar/ChartBoxContextToolbar";
import { ChartBoxDragger } from "./ChartBoxDragger";
import { useLocation, useSearchParams } from "react-router-dom";
import { DoOptimizePages } from "@coreapp/utils/DoOptimizePages";
import { Globals } from "@coreapp/CoreApp/Globals";
import { DISPLAY_MODES, SEARCH_PARAM_DISPLAY_KEY } from "@coreapp/constants";

const marginData = () => [
  {
    id: "0",
    position: {
      x: 0,
      y: 0
    },
    show: false
  },
  {
    id: "1",
    position: {
      x: 0,
      y: 0
    },
    show: false
  }
];

export const ChartCanvas = () => {
  const wsMgrContext = useWSMgrContext();
  const { chartDocument } = useChartDocumentContext();
  const [margins, setMargins] = useState(marginData);
  const [announcement, setAnnouncement] = useState("");
  const baseCanvasRef = useRef(null);
  const {
    setNodeRef,
    listeners,
    attributes,
    node: canvasRef
  } = useDroppable({
    id: `canvas`,
    data: {}
  });
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useTopic("BoundaryChange", () => {
    resetMargins();
    syncStateWithPageManager();
  });

  useTopic("canvasAnnounce", message => setAnnouncement(message ?? ""));

  useEffect(() => {
    resetMargins();
    syncStateWithPageManager();
  }, [chartDocument]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const canShowCanvas = isCanvasRoute(location.pathname);
    const display = searchParams.get(SEARCH_PARAM_DISPLAY_KEY);
    const isCoveredByDisplay =
      display === DISPLAY_MODES.TABLE || display === DISPLAY_MODES.DIRECTORY;
    const shouldHideCanvas = !canShowCanvas || isCoveredByDisplay;

    canvasRef.current.role = shouldHideCanvas ? undefined : "main";
    const hasKeyboardHelp = !shouldHideCanvas && Globals._uiConfig.InChartKeyboardNavMode();
    if (hasKeyboardHelp)
      baseCanvasRef.current.setAttribute("aria-describedby", "oc-chart-keyboard-help");
    else baseCanvasRef.current.removeAttribute("aria-describedby");
    canvasRef.current.style.visibility = shouldHideCanvas ? "hidden" : "visible";
  }, [location]);

  const getCanvasScaleFactor = () => {
    const wsMgr = GetWSMgr();
    const canvasPanel = wsMgr?.fCanvasPanel;
    if (!canvasPanel) return 1.0;
    return canvasPanel.fScaleFactor;
  };

  const handleDragEnd = ev => {
    const scaleFactor = getCanvasScaleFactor();
    const activeId = ev.active.id;
    const unscaledDelta = ev.delta.y ?? 0;
    const deltaY = Math.floor(unscaledDelta * (1 / scaleFactor));

    const margin = margins.find(x => x.id === activeId);
    margin.position.y += deltaY;

    updatePageManager(activeId, deltaY);

    const _margins = margins.map(x => {
      if (x.id === margin.id) return margin;
      return x;
    });

    setMargins(_margins);
  };

  const getPageMgr = () => {
    const wsMgr = GetWSMgr();
    const doc = wsMgr?.GetDoc();
    return doc?.GetPageMgr();
  };

  const syncStateWithPageManager = () => {
    const wsMgr = GetWSMgr();
    const pm = getPageMgr();
    const showHF = pm?.GetShowHeaderFooter();
    const canvasPanel = wsMgr?.fCanvasPanel;
    const bgMgr = GetBGMgr();
    const marginBoundary = canvasPanel?.fMarginBoundry;

    if (
      !pm ||
      !marginBoundary ||
      !showHF ||
      !Globals._uiConfig.InBackgroundEditorMode() ||
      // Box layout editor replaces the canvas surface with its own panel;
      // background-editor margin overlays (page header/footer guides) must
      // not show through even though ?tool=background passenger rides along
      // to preserve the parent session on BLE exit.
      Globals._uiConfig.InBoxLayoutEditorMode() ||
      bgMgr?.IsViewAppliedBackground()
    ) {
      resetMargins();
      return;
    }

    const canvasContainer = document.getElementById("canvasContainer");
    const canvasRect = canvasContainer.getBoundingClientRect();

    const htmlOffsetY = canvasRect.top;
    const htmlOffsetX = canvasRect.left;
    const maxY = canvasRect.bottom;

    const headerH = pm.GetHeaderHeight();
    const footerH = pm.GetFooterHeight();

    const panY = canvasPanel.fPanY;
    const panX = canvasPanel.fPanX;

    const scaleFactor = getCanvasScaleFactor();

    let canvasLocationX =
      canvasPanel.fHorzOffset + (marginBoundary.fHorzOffset - panX) * scaleFactor;
    if (canvasLocationX * scaleFactor < 0) canvasLocationX = 0;

    const canvasLocationY =
      canvasPanel.fVertOffset + (marginBoundary.fVertOffset - panY) * scaleFactor;

    const endCanvasLocationX = canvasLocationX + marginBoundary.fWidth * scaleFactor;
    const endCanvasLocationY = canvasLocationY + marginBoundary.fHeight * scaleFactor;

    let width = endCanvasLocationX - canvasLocationX;

    if (width > canvasRect.width) {
      width = canvasRect.width;

      // Determine if capped width should remove the boundary offset when panned to end.
      const portWidth = canvasPanel.fPortWidth;
      const canvasWidth = canvasPanel.fCanvasSizeX;
      const unscaledPortWidth = Math.round(portWidth / scaleFactor);
      const maxPanX = canvasWidth - unscaledPortWidth;

      const remainingPanX = (maxPanX - panX) * scaleFactor;
      if (remainingPanX <= marginBoundary.fHorzOffset * scaleFactor) {
        if (remainingPanX === 0) width -= marginBoundary.fHorzOffset * scaleFactor;
        else width -= (marginBoundary.fHorzOffset - remainingPanX) * scaleFactor;
      }
    }

    let headerMargin = margins.find(x => x.id === "0");
    let footerMargin = margins.find(x => x.id === "1");

    headerMargin.position.y = htmlOffsetY + canvasLocationY + headerH * scaleFactor;
    headerMargin.position.x = canvasLocationX + htmlOffsetX;
    headerMargin.widthPx = `${width}px`;
    headerMargin.show = headerMargin.position.y > htmlOffsetY;

    footerMargin.position.y = htmlOffsetY + endCanvasLocationY - footerH * scaleFactor;
    footerMargin.position.x = canvasLocationX + htmlOffsetX;
    footerMargin.widthPx = `${width}px`;
    footerMargin.show = footerMargin.position.y < maxY;

    setMargins([headerMargin, footerMargin]);
  };

  const updatePageManager = (id, delta) => {
    const wsMgr = GetWSMgr();
    const canvasPanel = wsMgr?.fCanvasPanel;
    const pm = getPageMgr();

    const pageHeight = pm.GetMinPageHeight();
    const maxHeight = Math.floor(pageHeight * 0.33);

    if (id === "0") {
      const curH = pm.GetHeaderHeight();
      let newH = curH + delta;
      if (newH < 1) newH = 1;
      else if (newH > maxHeight) newH = maxHeight;
      pm.SetHeaderHeight(newH);
    }
    if (id === "1") {
      const curH = pm.GetFooterHeight();
      let newH = curH + -1 * delta;
      if (newH < 1) newH = 1;
      else if (newH > maxHeight) newH = maxHeight;
      pm.SetFooterHeight(newH);
    }

    syncStateWithPageManager();

    wsMgr.fBackgroundMgr.UpdateActiveBackground(null, pm.GetPageProps());

    canvasPanel.RecalculateChartLayout(null);
    DoOptimizePages(wsMgr);

    wsMgr.BroadcastTopic("PropertyChange");

    ReRenderCanvas();
  };

  const resetMargins = () => {
    const defaultState = marginData();
    setMargins(defaultState);
  };

  const renderMargins = () => {
    const wsMgr = GetWSMgr();
    const bgMgr = GetBGMgr();
    const canvasPanel = wsMgr?.fCanvasPanel;
    return (
      canvasPanel?.IsShown() && isNullish(Globals._publishRef) && !bgMgr?.IsViewAppliedBackground()
    );
  };

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      modifiers={[snapCenterToCursor, restrictToVerticalAxis, restrictToWindowEdges]}
    >
      <div
        className="theCanvas ocn-container-scrollbar"
        id="canvasContainer"
        ref={setNodeRef}
        {...listeners}
        {...attributes}
      >
        <canvas ref={baseCanvasRef} id="basecanvas" tabIndex="0" />
        <span id="oc-chart-keyboard-help" className="oc-sr-only oc-chart-canvas-sr">
          {appstr("Chart keyboard help")}
        </span>
        <div
          className="oc-sr-only oc-chart-canvas-sr"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement}
        </div>
        {renderMargins() &&
          margins.map(margin => (
            <CanvasMargin
              margin={margin}
              key={margin.id}
              dividerProps={{
                vertical: false
              }}
            />
          ))}
        <ChartBoxDragger />
        <ChartBoxContextToolbar />
      </div>
    </DndContext>
  );
};
